import { writable, derived } from 'svelte/store';

export type ContactStatusFilter =
    | 'never_contacted'
    | 'not_home'
    | 'refused'
    | 'spoke';

export type VoterHistoryFilter =
    | ''
    | 'g24'
    | 'p26'
    | 'dropoff'; // voted G22 but not G24

export type PetitionFilter = '' | 'signed' | 'not_signed';

export type VotedFilter = '' | 'voted' | 'not_voted';

// 'no_response' = delivered or reached; 'none' = no consultant data.
export type TextOutreachFilter =
    '' | 'opted_in' | 'opted_out' | 'no_response' | 'not_contacted' | 'none';

export const VOTE_METHODS = ['mail', 'early', 'election_day'] as const;
export type UsualVoteMethod = typeof VOTE_METHODS[number];

export interface Filters {
    status: ContactStatusFilter[];        // empty = all
    support_min: number | null;           // 1-5
    support_max: number | null;
    county: string;                       // '' = all
    party: string[];                      // [] = all
    gender: string[];                     // [] = all
    precinct: string[];                   // [] = all
    usual_vote_method: UsualVoteMethod[]; // [] = all
    age_min: number | null;
    age_max: number | null;
    voter_history: VoterHistoryFilter;
    petition: PetitionFilter;
    voted: VotedFilter;                   // early/absentee ballot accepted
    household_partly_voted: boolean;      // household has voted and not-voted members
    text_outreach: TextOutreachFilter;    // consultant SMS state
    show_dnc: boolean;
    my_contacts_only: boolean;
    has_callback: boolean;
    search: string;                       // name or address substring
}

export const defaultFilters: Filters = {
    status: [],
    support_min: null,
    support_max: null,
    county: '',
    party: [],
    gender: [],
    precinct: [],
    usual_vote_method: [],
    age_min: null,
    age_max: null,
    voter_history: '',
    petition: '',
    voted: '',
    household_partly_voted: false,
    text_outreach: '',
    show_dnc: false,
    my_contacts_only: false,
    has_callback: false,
    search: '',
};

export const filters = writable<Filters>({ ...defaultFilters });

export function resetFilters(): void {
    filters.set({ ...defaultFilters });
}

// Counts how many filter fields differ from defaults (for badge on filter button)
export const activeCount = derived(filters, ($f) => {
    let n = 0;
    if ($f.status.length > 0) n++;
    if ($f.support_min !== null || $f.support_max !== null) n++;
    if ($f.county !== '') n++;
    if ($f.party.length > 0) n++;
    if ($f.gender.length > 0) n++;
    if ($f.precinct.length > 0) n++;
    if ($f.usual_vote_method.length > 0) n++;
    if ($f.age_min !== null || $f.age_max !== null) n++;
    if ($f.voter_history !== '') n++;
    if ($f.petition !== '') n++;
    if ($f.voted !== '') n++;
    if ($f.household_partly_voted) n++;
    if ($f.text_outreach !== '') n++;
    if ($f.show_dnc) n++;
    if ($f.my_contacts_only) n++;
    if ($f.has_callback) n++;
    if ($f.search !== '') n++;
    return n;
});

/** Convert filters to GET /api/voters query params */
export function filtersToParams($f: Filters): URLSearchParams {
    const p = new URLSearchParams();
    if ($f.status.length > 0) p.set('contact_status', $f.status.join(','));
    if ($f.support_min !== null) p.set('support_min', String($f.support_min));
    if ($f.support_max !== null) p.set('support_max', String($f.support_max));
    if ($f.county !== '') p.set('county', $f.county);
    if ($f.party.length > 0) p.set('party', $f.party.join(','));
    if ($f.gender.length > 0) p.set('gender', $f.gender.join(','));
    if ($f.precinct.length > 0) p.set('precinct', $f.precinct.join(','));
    if ($f.usual_vote_method.length > 0)
        p.set('usual_vote_method', $f.usual_vote_method.join(','));
    if ($f.age_min !== null) p.set('age_min', String($f.age_min));
    if ($f.age_max !== null) p.set('age_max', String($f.age_max));
    if ($f.voter_history !== '') p.set('voter_history', $f.voter_history);
    if ($f.petition !== '') p.set('petition', $f.petition);
    if ($f.voted !== '') p.set('voted', $f.voted);
    if ($f.household_partly_voted) p.set('household_voted', 'partial');
    if ($f.text_outreach !== '') p.set('text_outreach', $f.text_outreach);
    if (!$f.show_dnc) p.set('exclude_dnc', '1');
    if ($f.my_contacts_only) p.set('my_contacts_only', '1');
    if ($f.has_callback) p.set('has_callback', '1');
    if ($f.search !== '') p.set('search', $f.search);
    return p;
}
